import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { User } from './models/user';
import { CustomizationService } from './services/customization.service';
import { ErrorService } from './services/error.service';
import { GlobalService } from './services/global.service';
import { UserService } from './services/user.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable()
export class AppConfig implements OnDestroy {
	sub$ = new Subscription();

	constructor(private _swUpdate: SwUpdate,
				private _httpClient: HttpClient,
				private _customizationService: CustomizationService,
				private _userService: UserService,
				private _globalService: GlobalService,
				private _errorService: ErrorService) {
	}

	getAnonymousOptions() {
		let user = this._userService.getUser();
		this.sub$.add(this._globalService.getAnonymousGlobalOptions().subscribe({
			next: (globalSettings) => {
				if (user) {
					if (user.globalSettings) {
						Object.assign(user.globalSettings, user.globalSettings, globalSettings);
					} else {
						user.globalSettings = globalSettings;
					}
				} else {
					user = new User();
					user.globalSettings = globalSettings;
				}
				this._userService.updateUser(user);
			}, error: (error) => {
				this._errorService.error(error, 'getAnonymousGlobalOptions failed');
			}
		}));
	}

	getCustomSettings() {
		return this._httpClient.get<any>('./app/config/custom-settings.json');
	}

	public load() {
		return new Promise((resolve) => {
			if (this._swUpdate.isEnabled) {
				this.sub$.add(this._swUpdate.versionUpdates.pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
        ).subscribe(() => {
					this._swUpdate.unrecoverable.subscribe(event => {
						alert(
							`An error occurred that we cannot recover from:\n${event?.reason}\n\n` +
							'Please reload the page.');
					});
					alert('Client Portal has been updated to the latest version to ensure optimal functionality, stability, and user experience. If you had any open orders, please check to confirm your data has properly saved.');
					this._swUpdate.activateUpdate().then(() => document.location.reload());
				}));
			}
			// This is the default webservice api url, we will read in the customSettings.json and if the url is different
			// we will update it to the one in the customSettings.json and save to localStorage otherwise save default
			this.sub$.add(this.getCustomSettings().subscribe({
				next: (customSettings) => {
					if (customSettings.googleapis) {
						const script: any = document.getElementById('googleapis');
						if (document.getElementById('googleapis')) {
							script.src = customSettings.googleapis;
						}
					}
					if (customSettings.favicon) {
						if (document.getElementById('favicon')) {
							const link: any = document.getElementById('favicon');
							// setTimeout(()=>{
							link.href = customSettings.favicon;
							// }, 1000)
						}
					}
					if (customSettings.apiUrl && customSettings.apiUrl !== '/CxtWebService/CXTWCF.svc') {
						localStorage.setItem('Rapidship:apiUrl', customSettings.apiUrl);
						environment.apiUrl = customSettings.apiUrl;
					} else {
						localStorage.setItem('Rapidship:apiUrl', '/CxtWebService/CXTWCF.svc');
						environment.apiUrl = '/CxtWebService/CXTWCF.svc';
					}
					if (customSettings.hasOwnProperty('siteKey')) {
						environment.siteKey = customSettings.siteKey;
					}
					if (customSettings.hasOwnProperty('siteId')) {
						environment.siteId = customSettings.siteId;
					}
					this._customizationService.getStyleVariables(environment.siteId).subscribe({
						next: (styleVariables) => {
							const styleSheet = document.createElement('style');
							styleSheet.innerText = styleVariables;
							document.head.appendChild(styleSheet)
						}, error: (error) => {
							this._errorService.error(error, 'getStyleVariables failed', 'modal');
						}
					});
					this._customizationService.getCustomWidgets(environment.siteId).subscribe({
						next: (widgetConfig: any) => {
							if (widgetConfig) {
								// Save customer and page options
								widgetConfig = this._customizationService.saveWidgetConfig(widgetConfig);

								// Load script
								if (widgetConfig.loadScript) {
									const widgetScript = document.createElement('script');
									widgetScript.async = true;

									if (widgetConfig.scriptType === 'URL_REFERENCE') {
										widgetScript.src = widgetConfig.loadScript;
										widgetScript.id = 'ze-snippet'; // This name is required for ZenDesk widget to work.
									} else if (widgetConfig.scriptType === 'SCRIPT_BLOCK') {
										widgetScript.textContent = widgetConfig.loadScript;
									}

									document.body.appendChild(widgetScript);

									// When script is done loading, signal all the other components that it is ready.
									widgetScript.onload = () => {
										this._customizationService.widgetScriptLoaded$.next(true);
									};
								}
							}
						}, error: (error) => {
							this._errorService.error(error, 'getCustomWidgets failed', 'modal');
						}
					});

					this.getAnonymousOptions();
					resolve(true);
				}, error: (error) => {
					this._errorService.error(error, 'Get custom-settings failed', 'modal');
					localStorage.setItem('Rapidship:apiUrl', '/CxtWebService/CXTWCF.svc');
					environment.apiUrl = '/CxtWebService/CXTWCF.svc';
					this.getAnonymousOptions();
					resolve(true);
				}
			}));
		});
	}

  ngOnDestroy(): void {
    this.sub$.unsubscribe();
  }
}
